import type { LendingMode, LenderYields, BalanceData, AprData, LoopPostTradeMetrics } from '../types'
import { noOpResult, buildLoopResult } from '../utils'
import {
  getOrganicYieldDebtSwap,
  getRewardYieldDebtSwap,
  getIntrinsicYieldDebtSwap,
} from '../yield'

/**
 * Compute post-trade balance & APR metrics for a debt swap.
 *
 * Borrows `dollarIn` of a new debt asset and repays `dollarOut` of the existing debt.
 */
export function computeDebtSwapDeltas(
  dollarIn: number,
  dollarOut: number,
  sourceMode: LendingMode,
  targetMode: LendingMode,
  yieldParamsIn: LenderYields,
  yieldParamsOut: LenderYields,
  balance: BalanceData,
  apr: AprData,
  bfInFactor: number,
  bfOutFactor: number
): LoopPostTradeMetrics {
  if (dollarIn === 0 || dollarOut === 0) return noOpResult(balance, apr)

  const cashFlowDeposits = balance.deposits * apr.depositApr
  const cashFlowDebt = balance.debt * apr.borrowApr
  const rewardFlowDeposits = balance.deposits * (apr.rewardDepositApr ?? 0)
  const rewardFlowDebt = balance.debt * apr.rewardBorrowApr
  const intrinsicFlowDeposits = balance.deposits * apr.intrinsicDepositApr
  const intrinsicFlowDebt = balance.debt * apr.intrinsicBorrowApr

  const newBalance = { ...balance }
  const newApr = { ...apr }

  newBalance.debt += dollarOut - dollarIn
  newBalance.adjustedDebt += bfOutFactor * dollarOut - bfInFactor * dollarIn
  newBalance.nav += dollarOut - dollarIn

  ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldDebtSwap(
    dollarIn, dollarOut, sourceMode, targetMode,
    cashFlowDeposits, cashFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  newApr.rewards = getRewardYieldDebtSwap(
    dollarIn, dollarOut, sourceMode, targetMode,
    rewardFlowDeposits, rewardFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
    getIntrinsicYieldDebtSwap(
      dollarIn, dollarOut,
      intrinsicFlowDeposits, intrinsicFlowDebt,
      balance.deposits, balance.debt,
      yieldParamsIn, yieldParamsOut
    )

  return buildLoopResult(balance, newBalance, apr, newApr)
}
